/**
 * Shared Storage Utility Class
 * Used by both pod.js and custom-product-options.js to avoid code duplication
 */
class StorageUtil {
  constructor(prefix = 'app_') {
    this.prefix = prefix;
  }

  _key(key) {
    return this.prefix + key;
  }

  _now() {
    return Date.now();
  }

  _isExpired(item) {
    return item?.expires && this._now() > item.expires;
  }

  _safeParse(value) {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  _safeStringify(value) {
    return JSON.stringify(value);
  }

  set(key, value, options = {}) {
    const {
      ttl = 0, // time to live in ms
      storage = 'session' // 'session' | 'local' | 'cookie'
    } = options;

    const item = {
      value,
      expires: ttl > 0 ? this._now() + ttl : 0,
    };

    const str = this._safeStringify(item);
    const finalKey = this._key(key);

    try {
      if (storage === 'session' && typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(finalKey, str);
      } else if (storage === 'local' && typeof localStorage !== 'undefined') {
        localStorage.setItem(finalKey, str);
      } else {
        this._setCookie(finalKey, str, ttl);
      }
    } catch (e) {
      console.warn('Storage failed, falling back to cookie', e);
      this._setCookie(finalKey, str, ttl);
    }
  }

  get(key) {
    const finalKey = this._key(key);

    const sources = [
      () => typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(finalKey) : null,
      () => typeof localStorage !== 'undefined' ? localStorage.getItem(finalKey) : null,
      () => this._getCookie(finalKey),
    ];

    for (const source of sources) {
      const raw = source();
      const parsed = this._safeParse(raw);
      if (parsed) {
        if (!this._isExpired(parsed)) {
          return parsed.value;
        } else {
          this.remove(key);
          return null;
        }
      }
    }

    return null;
  }

  remove(key) {
    const finalKey = this._key(key);
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(finalKey);
    if (typeof localStorage !== 'undefined') localStorage.removeItem(finalKey);
    this._setCookie(finalKey, '', -1); // remove cookie
  }

  // ------------------ Cookie Utils ------------------

  _setCookie(name, value, ttl = 0) {
    let expires = '';
    if (ttl > 0) {
      const date = new Date();
      date.setTime(date.getTime() + ttl);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = `${name}=${encodeURIComponent(value)}${expires}; path=/`;
  }

  _getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let c of ca) {
      c = c.trim();
      if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length));
    }
    return null;
  }

  // ------------------ Advanced Methods for Pod Configuration ------------------

  /**
   * Get pod configuration with automatic format detection
   * @param {string} configKey - The configuration key (default: 'config')
   * @returns {Object|null} - The pod configuration data or null if not found
   */
  getPodConfig(configKey = 'config') {
    try {
      const podStorageKey = this._key(configKey);
      let podData = null;

      // Check sessionStorage first (pod's default storage)
      const sessionData = sessionStorage.getItem(podStorageKey);
      if (sessionData) {
        try {
          const parsed = JSON.parse(sessionData);
          if (parsed && parsed.value) {
            podData = parsed.value;
          }
        } catch (e) {
          console.error('Error parsing pod session data:', e);
        }
      }

      // Check localStorage if not found in sessionStorage
      if (!podData) {
        const localData = localStorage.getItem(podStorageKey);
        if (localData) {
          try {
            const parsed = JSON.parse(localData);
            if (parsed && parsed.value) {
              podData = parsed.value;
            }
          } catch (e) {
            console.error('Error parsing pod local data:', e);
          }
        }
      }

      // Also try cookie storage as fallback
      if (!podData) {
        const cookieData = this._getCookie(podStorageKey);
        if (cookieData) {
          try {
            const parsed = JSON.parse(decodeURIComponent(cookieData));
            if (parsed && parsed.value) {
              podData = parsed.value;
            }
          } catch (e) {
            console.error('Error parsing pod cookie data:', e);
          }
        }
      }

      return podData;
    } catch (error) {
      console.error('Error getting pod configuration:', error);
      return null;
    }
  }

  /**
   * Set pod configuration in the standard pod format
   * @param {string} configKey - The configuration key
   * @param {Object} configData - The configuration data
   * @param {Object} options - Storage options
   */
  setPodConfig(configKey = 'config', configData, options = {}) {
    const storageData = {
      value: configData,
      expires: options.ttl ? this._now() + options.ttl : 0
    };

    const finalKey = this._key(configKey);
    const storageType = options.storage || 'session';

    try {
      const dataStr = JSON.stringify(storageData);
      
      if (storageType === 'session' && typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(finalKey, dataStr);
      } else if (storageType === 'local' && typeof localStorage !== 'undefined') {
        localStorage.setItem(finalKey, dataStr);
      } else {
        this._setCookie(finalKey, dataStr, options.ttl || 0);
      }
    } catch (e) {
      console.error('Error setting pod configuration:', e);
    }
  }

  /**
   * Check if a specific storage key exists in any storage type
   * @param {string} key - The key to check
   * @returns {Object} - Object with existence status for each storage type
   */
  checkStorageStatus(key) {
    const finalKey = this._key(key);
    
    return {
      sessionStorage: typeof sessionStorage !== 'undefined' && sessionStorage.getItem(finalKey) !== null,
      localStorage: typeof localStorage !== 'undefined' && localStorage.getItem(finalKey) !== null,
      cookie: this._getCookie(finalKey) !== null,
      key: finalKey
    };
  }
}

// Export for use in other files
if (typeof window !== 'undefined') {
  window.StorageUtil = StorageUtil;
}

// Export for Node.js environments
if (typeof module !== 'undefined' && module.exports) {
  module.exports = StorageUtil;
} 